package com.evoapps.evoshift.photo

import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberUpdatedState
import kotlinx.browser.document
import org.khronos.webgl.ArrayBuffer
import org.khronos.webgl.Int8Array
import org.w3c.dom.HTMLInputElement
import org.w3c.files.FileReader
import org.w3c.files.get

/**
 * The browser's file chooser (a hidden file input accepting images), opened by the click that asked
 * for it. On phones it also offers the camera. Only the first [PICK_READ_LIMIT] bytes are read.
 */
@Composable
internal actual fun rememberPlatformImagePicker(onPicked: OnImagePicked): ImagePicker {
    val callback = rememberUpdatedState(onPicked)
    return remember {
        object : ImagePicker {
            override val canTakePhoto: Boolean = false

            override fun pickFromGallery() = chooseFile { callback.value(it) }

            override fun takePhoto() = chooseFile { callback.value(it) }
        }
    }
}

private fun chooseFile(deliver: (ByteArray?) -> Unit) {
    val input = document.createElement("input") as HTMLInputElement
    input.type = "file"
    input.accept = "image/*"
    input.style.display = "none"
    input.onchange = {
        val file = input.files?.get(0)
        input.remove()
        if (file != null) {
            val reader = FileReader()
            reader.onload = { deliver(Int8Array(reader.result.unsafeCast<ArrayBuffer>()).unsafeCast<ByteArray>()) }
            reader.onerror = { deliver(null) }
            reader.readAsArrayBuffer(file.slice(0, PICK_READ_LIMIT))
        }
    }
    // Closing the chooser sends no event in every browser; the input then just stays detached.
    input.addEventListener("cancel", { input.remove() })
    document.body?.appendChild(input)
    input.click()
}
